import Button from 'components/Button';
import Input from 'components/Input';
import { useCallback, useEffect, useRef, useState } from 'react';
import usePresetStore from 'store/presetStore';
import { IContactInformation, ITollChargerWorkingTime, IOresundPayData } from 'types/apiTypes';
import DatePicker from 'components/DatePicker';
import { Link } from 'react-router-dom';
import { klona } from 'klona/lite';
import { getProperty, setProperty } from 'dot-prop';
import { z as zod, ZodIssue, ZodType } from 'zod';
import Select from 'components/Select';
import classNames from 'classnames';

// Must check if actorId isn't the current actorId
let currentActorId = '';

const zodEmptyString = zod.string().length(0);
const zodTimeString = (keyName: string) =>
  zod.string().regex(/^\d{2}:\d{2}:\d{2}$/, `Enter ${keyName} as hh:mm:ss`);

const workingTimeValidation = {
  startTime: zodTimeString('startTime').or(zodEmptyString).optional(),
  endTime: zodTimeString('endTime').or(zodEmptyString).optional(),
  timeZone: zod.string().or(zodEmptyString).optional(),
  weekdays: zod.string().or(zodEmptyString).optional(),
};

const contactInformationValidation = {
  contact: zod.string().or(zodEmptyString).optional(),
  telWorkingTime: zod.string().or(zodEmptyString).optional(),
  telOutsideWorkingTime: zod.string().or(zodEmptyString).optional(),
  mobile: zod.string().or(zodEmptyString).optional(),
  email: zod
    .string()
    .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email format')
    .or(zodEmptyString)
    .optional(),
  workingTimes: zod.array(zod.object(workingTimeValidation)).optional(),
};

const bankAccountValidation = {
  iban: zod.string().or(zodEmptyString).optional(),
  accountNumber: zod.string().or(zodEmptyString).optional(),
};

const bankDetailsInformationValidation = {
  bankAccount: zod.object(bankAccountValidation),
  bic: zod.string().or(zodEmptyString).optional(),
  currencyCode: zod.string().or(zodEmptyString).optional(),
};

const tollContextDataVersionValidation = {
  validFrom: zod
    .string()
    .regex(
      /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d(\.\d+)?([+-][0-2]\d:[0-5]\d|Z)$/,
      'Please select a date'
    )
    .nullable()
    .optional(),
};

const entityIdValidation = {
  name: zod.string().min(2, 'Value must be at least 2 characters long'),
  countryCode: zod
    .string()
    .length(2, 'Value must be 2 characters long')
    .regex(/^[a-zA-Z]{2}$/, 'Use ISO 3166-1 alpha-2 format'),
  providerIdentifier: zod.number().min(0, 'The value cannot be less than 0'),
};

const formValidation = {
  id: zod.string(),
  entityId: zod.object(entityIdValidation),
  entityType: zod.union([
    zod
      .string()
      .transform((value) => (value === '' ? Number.NaN : Number(value)))
      .refine((value) => !isNaN(Number(value)), {
        error: 'Value must be 1 or 2',
      }),
    zod.number(),
  ]),
  nameLine1: zod.string().or(zodEmptyString).optional(),
  nameLine2: zod.string().or(zodEmptyString).optional(),
  addressLine: zod.string().or(zodEmptyString).optional(),
  zipCode: zod.string().or(zodEmptyString).optional(),
  city: zod.string().or(zodEmptyString).optional(),
  country: zod.string().or(zodEmptyString).optional(),
  countryCode: zod
    .string()
    .regex(/^[a-zA-Z]{2}$/, 'Use ISO 3166-1 alpha-2 format')
    .or(zodEmptyString)
    .optional(),
  actorId: zod
    .string()
    .length(6, 'Value must be 6 characters long')
    .refine(
      (val): boolean => {
        // Must check if actorId isn't the current actorId
        if (val === currentActorId) {
          return true;
        }
        // Check if actorId already exists
        return (
          usePresetStore
            .getState()
            .tollChargerValues.find((tollCharger) => tollCharger.value === val) === undefined
        );
      },
      {
        error: 'ActorId already exists',
      }
    ),
  website: zod.string().or(zodEmptyString).optional(),
  vatId: zod.string().or(zodEmptyString).optional(),
  invoiceEmail: zod
    .string()
    .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email format')
    .or(zodEmptyString)
    .optional(),
  tollScheme: zod.string().nonempty('tollScheme is required'),
  tollContextName: zod.string().or(zodEmptyString).optional(),
  bankDetailsInformation: zod.object(bankDetailsInformationValidation),
  tollContextDataVersion: zod.object(tollContextDataVersionValidation),
  mainContactInformation: zod.object(contactInformationValidation).optional(),
  customerServiceContactInformation: zod.object(contactInformationValidation).optional(),
  itContactInformation: zod.object(contactInformationValidation).optional(),
  operationalContactInformation: zod.object(contactInformationValidation).optional(),
  commercialContactInformation: zod.object(contactInformationValidation).optional(),
};

const zodSchema = zod.object(formValidation);

const defaultFormData: Partial<IOresundPayData> = {
  // Fields to just pass on
  id: 'new',

  // Editable fields

  //@ts-expect-error missing props for entityId
  entityId: {},
  entityType: 2,
  nameLine1: '',
  nameLine2: '',
  addressLine: '',
  zipCode: '',
  city: '',
  country: '',
  countryCode: '',
  actorId: '',
  tollContextName: '',
  website: '',
  vatId: '',
  invoiceEmail: '',
  bankDetailsInformation: {
    bankAccount: {
      iban: '',
      accountNumber: '',
    },
    bic: '',
    currencyCode: '',
  },
  tollContextDataVersion: {
    validFrom: null,
    version: '',
  },
  mainContactInformation: {
    contact: '',
    telWorkingTime: '',
    telOutsideWorkingTime: '',
    mobile: '',
    email: '',

    workingTimes: [] as ITollChargerWorkingTime[],
  },
  customerServiceContactInformation: {
    contact: '',
    telWorkingTime: '',
    telOutsideWorkingTime: '',
    mobile: '',
    email: '',

    workingTimes: [] as ITollChargerWorkingTime[],
  },
  itContactInformation: {
    contact: '',
    telWorkingTime: '',
    telOutsideWorkingTime: '',
    mobile: '',
    email: '',

    workingTimes: [] as ITollChargerWorkingTime[],
  },
  operationalContactInformation: {
    contact: '',
    telWorkingTime: '',
    telOutsideWorkingTime: '',
    mobile: '',
    email: '',

    workingTimes: [] as ITollChargerWorkingTime[],
  },
  commercialContactInformation: {
    contact: '',
    telWorkingTime: '',
    telOutsideWorkingTime: '',
    mobile: '',
    email: '',

    workingTimes: [] as ITollChargerWorkingTime[],
  },
};
interface ContactInformationProps {
  title: string;
  mainKey: string;
  contactState: IContactInformation | undefined;
  addWorkingTime: (key: string) => void;
  removeWorkingTime: (key: string, index: number) => void;
  errorMessages: Record<string, string>;
  onBlurValidation: (key: string, zodValidation: ZodType) => void;
  onChangeInput: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const ContactInformation = ({
  title,
  mainKey,
  contactState,
  addWorkingTime,
  removeWorkingTime,
  errorMessages,
  onBlurValidation,
  onChangeInput,
}: ContactInformationProps) => {
  return (
    <div className="my-4 grid grid-cols-1 gap-4 rounded-md border border-neutral-100 bg-neutral-50 p-4 lg:grid-cols-2">
      <h3 className="col-span-full pb-1 text-xl font-semibold">{title}</h3>
      <Input
        label="contact"
        name={mainKey + '.contact'}
        value={contactState?.contact}
        onChange={onChangeInput}
        error={errorMessages[mainKey + '.contact']}
        onBlur={() => onBlurValidation(mainKey + '.contact', contactInformationValidation.contact)}
      />
      <Input
        label="telWorkingTime"
        name={mainKey + '.telWorkingTime'}
        value={contactState?.telWorkingTime}
        onChange={onChangeInput}
        error={errorMessages[mainKey + '.telWorkingTime']}
        onBlur={() =>
          onBlurValidation(mainKey + '.telWorkingTime', contactInformationValidation.telWorkingTime)
        }
      />
      <Input
        label="telOutsideWorkingTime"
        name={mainKey + '.telOutsideWorkingTime'}
        value={contactState?.telOutsideWorkingTime}
        onChange={onChangeInput}
        error={errorMessages[mainKey + '.telOutsideWorkingTime']}
        onBlur={() =>
          onBlurValidation(
            mainKey + '.telOutsideWorkingTime',
            contactInformationValidation.telOutsideWorkingTime
          )
        }
      />
      <Input
        label="mobile"
        name={mainKey + '.mobile'}
        value={contactState?.mobile}
        onChange={onChangeInput}
        error={errorMessages[mainKey + '.mobile']}
        onBlur={() => onBlurValidation(mainKey + '.mobile', contactInformationValidation.mobile)}
      />
      <Input
        label="email"
        name={mainKey + '.email'}
        value={contactState?.email}
        onChange={onChangeInput}
        error={errorMessages[mainKey + '.email']}
        onBlur={() => onBlurValidation(mainKey + '.email', contactInformationValidation.email)}
      />
      <h4 className="col-span-full pt-4 pb-0 text-lg font-semibold">Working time</h4>
      {contactState?.workingTimes.map((wt, index) => (
        <div
          className="col-span-full grid grid-cols-2 gap-4 rounded-md border border-green-800 p-4 lg:grid-cols-4"
          key={index}
        >
          <Input
            label="startTime"
            name={`${mainKey}.workingTimes[${index}].startTime`}
            placeholder="hh:mm:ss"
            value={wt.startTime}
            onChange={onChangeInput}
            error={errorMessages[`${mainKey}.workingTimes[${index}].startTime`]}
            onBlur={() =>
              onBlurValidation(
                `${mainKey}.workingTimes[${index}].startTime`,
                workingTimeValidation.startTime
              )
            }
          />
          <Input
            label="endTime"
            name={`${mainKey}.workingTimes[${index}].endTime`}
            placeholder="hh:mm:ss"
            value={wt.endTime}
            onChange={onChangeInput}
            error={errorMessages[`${mainKey}.workingTimes[${index}].endTime`]}
            onBlur={() =>
              onBlurValidation(
                `${mainKey}.workingTimes[${index}].endTime`,
                workingTimeValidation.endTime
              )
            }
          />
          <Input
            label="timeZone"
            name={`${mainKey}.workingTimes[${index}].timeZone`}
            value={wt.timeZone}
            onChange={onChangeInput}
            error={errorMessages[`${mainKey}.workingTimes[${index}].timeZone`]}
            onBlur={() =>
              onBlurValidation(
                `${mainKey}.workingTimes[${index}].timeZone`,
                workingTimeValidation.timeZone
              )
            }
          />
          <Input
            label="weekdays"
            name={`${mainKey}.workingTimes[${index}].weekdays`}
            value={wt.weekdays}
            onChange={onChangeInput}
            error={errorMessages[`${mainKey}.workingTimes[${index}].weekdays`]}
            onBlur={() =>
              onBlurValidation(
                `${mainKey}.workingTimes[${index}].weekdays`,
                workingTimeValidation.weekdays
              )
            }
          />
          <div className="col-span-full flex justify-end">
            <Button
              size="sm"
              variant="danger"
              onClick={(e) => {
                e.preventDefault();
                removeWorkingTime(mainKey + '.workingTimes', index);
              }}
            >
              Remove
            </Button>
          </div>
        </div>
      ))}
      <div className="col-span-full flex justify-end">
        <Button
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            addWorkingTime(mainKey + '.workingTimes');
          }}
        >
          Add working time
        </Button>
      </div>
    </div>
  );
};
interface IOresundPayFormProps {
  formData?: IOresundPayData;
  loading?: boolean;
  onSubmit?: (newData: IOresundPayData) => void;
}

const OresundPayForm = ({ formData, loading = false, onSubmit }: IOresundPayFormProps) => {
  const { tollSchemeValues } = usePresetStore();
  const [prevFormData, setPrevFormData] = useState(formData);
  const [localFormState, setLocalFormState] = useState<Partial<IOresundPayData>>(
    formData ?? defaultFormData
  );
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});

  const tollSchemeRef = useRef<HTMLSelectElement>(null);

  if (prevFormData !== formData) {
    setPrevFormData(formData);
    if (formData) {
      const clonedData = klona(formData);

      // Set null values to empty string for new fields where no data exists yet
      if (clonedData.bankDetailsInformation.bankAccount.accountNumber === null) {
        clonedData.bankDetailsInformation.bankAccount.accountNumber = '';
      }

      // mainContactInformation
      if (clonedData.mainContactInformation.telOutsideWorkingTime === null) {
        clonedData.mainContactInformation.telOutsideWorkingTime = '';
      }
      if (clonedData.mainContactInformation.mobile === null) {
        clonedData.mainContactInformation.mobile = '';
      }

      // customerServiceContactInformation
      if (clonedData.customerServiceContactInformation.telOutsideWorkingTime === null) {
        clonedData.customerServiceContactInformation.telOutsideWorkingTime = '';
      }
      if (clonedData.customerServiceContactInformation.mobile === null) {
        clonedData.customerServiceContactInformation.mobile = '';
      }

      // itContactInformation
      if (clonedData.itContactInformation.telOutsideWorkingTime === null) {
        clonedData.itContactInformation.telOutsideWorkingTime = '';
      }
      if (clonedData.itContactInformation.mobile === null) {
        clonedData.itContactInformation.mobile = '';
      }

      // operationalContactInformation
      if (clonedData.operationalContactInformation.telOutsideWorkingTime === null) {
        clonedData.operationalContactInformation.telOutsideWorkingTime = '';
      }
      if (clonedData.operationalContactInformation.mobile === null) {
        clonedData.operationalContactInformation.mobile = '';
      }

      // commercialContactInformation
      if (clonedData.commercialContactInformation.telOutsideWorkingTime === null) {
        clonedData.commercialContactInformation.telOutsideWorkingTime = '';
      }
      if (clonedData.commercialContactInformation.mobile === null) {
        clonedData.commercialContactInformation.mobile = '';
      }

      setLocalFormState(clonedData);
    }
  }

  useEffect(() => {
    if (formData?.actorId) {
      currentActorId = formData.actorId;
    }
  }, [formData]);

  const onBlurValidation = useCallback(
    (key: string, zodValidation: ZodType) => {
      const value = getProperty(localFormState, key);
      const result = zodValidation.safeParse(value);

      setErrorMessages((prevErrorMessages) => {
        const newErrorMessages: Record<string, string> = { ...prevErrorMessages };
        if (!result.success) {
          // Add error message
          newErrorMessages[key] = result.error.issues[0].message;
        } else {
          // remove error message
          delete newErrorMessages[key];
        }
        return newErrorMessages;
      });
      return result.success;
    },
    [localFormState]
  );
  const addWorkingTime = (key: string) => {
    const newLocalFormState = klona(localFormState);

    const currentWorkingTimes: ITollChargerWorkingTime[] =
      getProperty(newLocalFormState, key, []) ?? [];

    setProperty(newLocalFormState, key, [
      ...currentWorkingTimes,
      {
        weekdays: '',
        timeZone: '',
        startTime: '',
        endTime: '',
      } as ITollChargerWorkingTime,
    ]);
    setLocalFormState(newLocalFormState);
  };

  const removeWorkingTime = (key: string, index: number) => {
    const newLocalFormState = klona(localFormState);

    (getProperty(newLocalFormState, key, []) ?? []).splice(index, 1);

    setLocalFormState(newLocalFormState);
  };

  const onChangeInput = (
    event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const name: string = event.target.name;
    const value: string = event.target.value;
    const newLocalFormState = klona(localFormState);
    setProperty(newLocalFormState, name, value);
    setLocalFormState(newLocalFormState);
  };

  const onChangeInputNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name: string = event.target.name;
    const value: number | string = /^\d+$/.test(event.target.value)
      ? parseInt(event.target.value)
      : event.target.value;
    const newLocalFormState = klona(localFormState);
    if (value === '') {
      setProperty(newLocalFormState, name, undefined);
    } else {
      setProperty(newLocalFormState, name, value);
    }

    setLocalFormState(newLocalFormState);
  };

  const onChangeDatePickerFilter = (newDate: Date | undefined, name: string) => {
    const newLocalFormState = klona(localFormState);
    setProperty(newLocalFormState, name, newDate?.toISOString() ?? null);
    setLocalFormState(newLocalFormState);
  };

  const formatZodErrors = (zodErrors: ZodIssue[]) => {
    const newErrors: Record<string, string> = {};

    zodErrors.forEach((zodError) => {
      const path = zodError.path
        .map((path) => {
          if (typeof path === 'number') {
            return `[${path}]`;
          }
          return path;
        })
        .join('.')
        .replace(/\.\[/g, '[');

      newErrors[path] = zodError.message;
    });
    setErrorMessages(newErrors);
  };

  const onSubmitForm = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      // Validate form
      const safeParsedZod = zodSchema.safeParse(localFormState);

      if (!safeParsedZod.success) {
        formatZodErrors(safeParsedZod.error.issues);
      }

      if (safeParsedZod.success && onSubmit) {
        const base64WebsiteData = {
          ...localFormState,
          website: btoa(localFormState.website ?? ''),
        } as IOresundPayData;
        onSubmit(base64WebsiteData);
      }
    },
    [localFormState, onSubmit]
  );

  return (
    <form onSubmit={onSubmitForm} className="max-w-[800px]">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <h3 className="col-span-full pt-4 pb-1 text-xl font-semibold">
          Toll Service Provider entity
        </h3>
        <Input
          label="entityId"
          value={`${localFormState.entityId?.name ?? ''} (${localFormState.entityId?.countryCode ?? ''}-${localFormState.entityId?.providerIdentifier ?? ''})`}
          readOnly
          disabled
        />
        <Input
          label="name"
          name="entityId.name"
          value={localFormState.entityId?.name}
          onChange={onChangeInput}
          error={errorMessages['entityId.name']}
          onBlur={() => onBlurValidation('entityId.name', entityIdValidation.name)}
        />
        <Input
          label="countryCode"
          name="entityId.countryCode"
          value={localFormState.entityId?.countryCode}
          disabled
          readOnly
          onChange={(event) => {
            onChangeInput({
              // Convert value to uppercase
              // @ts-expect-error target missing properties we don't need
              target: { name: event.target.name, value: event.target.value.toUpperCase() },
            });
          }}
          error={errorMessages['entityId.countryCode']}
          onBlur={() => onBlurValidation('entityId.countryCode', entityIdValidation.countryCode)}
        />
        <Input
          label="providerIdentifier"
          name="entityId.providerIdentifier"
          value={localFormState.entityId?.providerIdentifier}
          disabled
          readOnly
          onChange={onChangeInputNumber}
          error={errorMessages['entityId.providerIdentifier']}
          onBlur={() =>
            onBlurValidation('entityId.providerIdentifier', entityIdValidation.providerIdentifier)
          }
          type="number"
        />
        <Input
          label="actorId"
          name="actorId"
          value={localFormState.actorId}
          disabled
          readOnly
          error={errorMessages['actorId']}
        />

        <Select
          inputClassName={classNames(!localFormState.tollScheme && 'text-neutral-500')}
          value={localFormState.tollScheme ?? ''}
          label="tollScheme"
          name="tollScheme"
          options={[{ label: '-- No value selected --', value: '' }, ...tollSchemeValues]}
          ref={tollSchemeRef}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
            onChangeInput(event);
            setTimeout(() => {
              tollSchemeRef.current?.blur();
            }, 0);
          }}
          error={errorMessages['tollScheme']}
          onBlur={() => onBlurValidation('tollScheme', formValidation.tollScheme)}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <h3 className="col-span-full pt-4 pb-1 text-xl font-semibold">Contact info</h3>

        <Input
          label="nameLine1"
          name="nameLine1"
          value={localFormState.nameLine1}
          onChange={onChangeInput}
          error={errorMessages['nameLine1']}
          onBlur={() => onBlurValidation('nameLine1', formValidation.nameLine1)}
        />
        <Input
          label="nameLine2"
          name="nameLine2"
          value={localFormState.nameLine2}
          onChange={onChangeInput}
          error={errorMessages['nameLine2']}
          onBlur={() => onBlurValidation('nameLine2', formValidation.nameLine2)}
        />
        <Input
          label="addressLine"
          name="addressLine"
          value={localFormState.addressLine}
          onChange={onChangeInput}
          error={errorMessages['addressLine']}
          onBlur={() => onBlurValidation('addressLine', formValidation.addressLine)}
        />
        <Input
          label="zipCode"
          name="zipCode"
          value={localFormState.zipCode}
          onChange={onChangeInput}
          error={errorMessages['zipCode']}
          onBlur={() => onBlurValidation('zipCode', formValidation.zipCode)}
        />
        <Input
          label="city"
          name="city"
          value={localFormState.city}
          onChange={onChangeInput}
          error={errorMessages['city']}
          onBlur={() => onBlurValidation('city', formValidation.city)}
        />
        <Input
          label="country"
          name="country"
          value={localFormState.country}
          onChange={onChangeInput}
          error={errorMessages['country']}
          onBlur={() => onBlurValidation('country', formValidation.country)}
        />
        <Input
          label="countryCode"
          name="countryCode"
          value={localFormState.countryCode}
          onChange={onChangeInput}
          error={errorMessages['countryCode']}
          onBlur={() => onBlurValidation('countryCode', formValidation.countryCode)}
        />
        <Input
          label="website"
          name="website"
          value={localFormState.website}
          onChange={onChangeInput}
          error={errorMessages['website']}
          onBlur={() => onBlurValidation('website', formValidation.website)}
        />
        <Input
          label="invoiceEmail"
          name="invoiceEmail"
          value={localFormState.invoiceEmail}
          onChange={onChangeInput}
          error={errorMessages['invoiceEmail']}
          onBlur={() => onBlurValidation('invoiceEmail', formValidation.invoiceEmail)}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <h3 className="col-span-full pt-4 pb-1 text-xl font-semibold">Toll Context</h3>
        <DatePicker
          label="validFrom"
          name="tollContextDataVersion.validFrom"
          value={
            localFormState.tollContextDataVersion?.validFrom
              ? new Date(localFormState.tollContextDataVersion.validFrom)
              : undefined
          }
          onChange={onChangeDatePickerFilter}
          error={errorMessages['tollContextDataVersion.validFrom']}
          onBlur={() => {
            onBlurValidation(
              'tollContextDataVersion.validFrom',
              tollContextDataVersionValidation.validFrom
            );
          }}
        />
        <Input
          label="version"
          name="tollContextDataVersion.version"
          value={localFormState.tollContextDataVersion?.version}
          disabled
          readOnly
        />
        <Input
          label="name"
          name="tollContextName"
          value={localFormState.tollContextName}
          onChange={onChangeInput}
          error={errorMessages['tollContextName']}
          onBlur={() => onBlurValidation('tollContextName', formValidation.tollContextName)}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <h3 className="col-span-full pt-4 pb-1 text-xl font-semibold">Bank Details</h3>
        <Input
          label="iban"
          name="bankDetailsInformation.bankAccount.iban"
          value={localFormState.bankDetailsInformation?.bankAccount?.iban}
          onChange={onChangeInput}
          error={errorMessages['bankDetailsInformation.bankAccount.iban']}
          onBlur={() =>
            onBlurValidation('bankDetailsInformation.bankAccount.iban', bankAccountValidation.iban)
          }
        />
        <Input
          label="accountNumber"
          name="bankDetailsInformation.bankAccount.accountNumber"
          value={localFormState.bankDetailsInformation?.bankAccount?.accountNumber}
          onChange={onChangeInput}
          error={errorMessages['bankDetailsInformation.bankAccount.accountNumber']}
          onBlur={() =>
            onBlurValidation(
              'bankDetailsInformation.bankAccount.accountNumber',
              bankAccountValidation.accountNumber
            )
          }
        />
        <Input
          label="bic"
          name="bankDetailsInformation.bic"
          value={localFormState.bankDetailsInformation?.bic}
          onChange={onChangeInput}
          error={errorMessages['bankDetailsInformation.bic']}
          onBlur={() =>
            onBlurValidation('bankDetailsInformation.bic', bankDetailsInformationValidation.bic)
          }
        />
        <Input
          label="currencyCode"
          name="bankDetailsInformation.currencyCode"
          value={localFormState.bankDetailsInformation?.currencyCode}
          onChange={onChangeInput}
          error={errorMessages['bankDetailsInformation.currencyCode']}
          onBlur={() =>
            onBlurValidation(
              'bankDetailsInformation.currencyCode',
              bankDetailsInformationValidation.currencyCode
            )
          }
        />
        <Input
          label="vatId"
          name="vatId"
          value={localFormState.vatId}
          onChange={onChangeInput}
          error={errorMessages['vatId']}
          onBlur={() => onBlurValidation('vatId', formValidation.vatId)}
        />
      </div>

      <ContactInformation
        title="mainContactInformation"
        mainKey="mainContactInformation"
        contactState={localFormState.mainContactInformation}
        addWorkingTime={addWorkingTime}
        removeWorkingTime={removeWorkingTime}
        errorMessages={errorMessages}
        onBlurValidation={onBlurValidation}
        onChangeInput={onChangeInput}
      />

      <ContactInformation
        title="customerServiceContactInformation"
        mainKey="customerServiceContactInformation"
        contactState={localFormState.customerServiceContactInformation}
        addWorkingTime={addWorkingTime}
        removeWorkingTime={removeWorkingTime}
        errorMessages={errorMessages}
        onBlurValidation={onBlurValidation}
        onChangeInput={onChangeInput}
      />

      <ContactInformation
        title="itContactInformation"
        mainKey="itContactInformation"
        contactState={localFormState.itContactInformation}
        addWorkingTime={addWorkingTime}
        removeWorkingTime={removeWorkingTime}
        errorMessages={errorMessages}
        onBlurValidation={onBlurValidation}
        onChangeInput={onChangeInput}
      />

      <ContactInformation
        title="operationalContactInformation"
        mainKey="operationalContactInformation"
        contactState={localFormState.operationalContactInformation}
        addWorkingTime={addWorkingTime}
        removeWorkingTime={removeWorkingTime}
        errorMessages={errorMessages}
        onBlurValidation={onBlurValidation}
        onChangeInput={onChangeInput}
      />

      <ContactInformation
        title="commercialContactInformation"
        mainKey="commercialContactInformation"
        contactState={localFormState.commercialContactInformation}
        addWorkingTime={addWorkingTime}
        removeWorkingTime={removeWorkingTime}
        errorMessages={errorMessages}
        onBlurValidation={onBlurValidation}
        onChangeInput={onChangeInput}
      />

      <div className="flex justify-between py-8">
        <Link to="./..">
          <Button variant="ghost" type="button" disabled={loading}>
            Cancel
          </Button>
        </Link>
        <Button variant="primary" type="submit" spinner={loading}>
          Save
        </Button>
      </div>
    </form>
  );
};

export default OresundPayForm;
